if (!window.Maximize.loadedScript.includes("product-card.js")) {
  window.Maximize.loadedScript.push("product-card.js");

  // The swatch chips stand for the option values themselves, which do not change with
  // the selection: their look must survive a swatch click. Merging the refreshed style
  // in, instead of replacing the attribute, keeps every custom property the card already
  // resolved (--swatch-image-value above all) when the refreshed markup omits it.
  const mergeSwatchOptionStyle = (label, sourceStyle) => {
    sourceStyle.split(";").forEach((declaration) => {
      const separator = declaration.indexOf(":");
      if (separator < 0) return;

      const property = declaration.slice(0, separator).trim();
      const value = declaration.slice(separator + 1).trim();
      if (!property || !value) return;

      label.style.setProperty(property, value);
    });
  };

  // An unavailable swatch stays crossed out (".disabled" class) and is not selectable:
  // the radio is disabled and its label leaves the tab order and is flagged for
  // assistive technologies.
  const setSwatchOptionDisabled = (input, label, disabled) => {
    input.classList[disabled ? 'add' : 'remove']('disabled');
    input.disabled = disabled;
    if (disabled) {
      label.setAttribute('aria-disabled', 'true');
      label.setAttribute('tabindex', '-1');
    } else {
      label.removeAttribute('aria-disabled');
      label.setAttribute('tabindex', '0');
    }
  };

  // Same normalisation as Liquid's `handle` filter, used to compare option names and values
  // with the filter.v.option.<handle> parameters of the collection and search URLs.
  const handleize = (value) => String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  class ProductCardSwatchOption extends HTMLElement {
    static cacheData = {}
    constructor() {
      super();
      this.productCardWrapperEle = this.closest(`.product-card__wrapper`);
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.optionId = this.dataset.optionId;
      this.productCardId = this.dataset.productCardId;
      this.productUrl = this.dataset.productUrl;
      this.effectImage = this.dataset.effectImage;
      this.isShowEDT = this.hasAttribute('data-show-edt') && this.dataset.showEdt === 'true';
      this.optionsSelectedValues = [];
      this.currentVariantId = Number(this.dataset.variantId);
      try {
        this.cardData = JSON.parse(this.querySelector('script[data-card-variants]')?.textContent || "null");
      } catch (error) {
        this.cardData = null;
      }

      this.optionValueInputElements = Array.from(this.querySelectorAll(`.variant-option__input`));
      this.optionLabelElements = Array.from(this.querySelectorAll(`.swatch-option__label`));
      const listSelectedOptionValue = this.optionValueInputElements.filter(ele => ele.classList.contains('selected'));
      listSelectedOptionValue.forEach((input) => {
        const position = input.dataset.optionPosition;
        this.optionsSelectedValues[position - 1] = input.dataset.optionValueId;
      })
    }

    connectedCallback() {
      this.optionLabelElements.forEach(el => {
        el.addEventListener('click', this.handleClickOptionLabel.bind(this));

        el.addEventListener('keyup', el => {
          if (el.key === 'Enter' || el.key === ' ') {
            this.handleClickOptionLabel.bind(this);
          }
        })
      })
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      // Unavailable option: no selection, no image change, no navigation.
      if (option.disabled || option.classList.contains('disabled')) return;
      const position = option.dataset.optionPosition;
      const idx = position - 1;
      let listInputSamePosition = this.optionValueInputElements.filter((el) => el.dataset.optionPosition === position);
      listInputSamePosition.forEach((el) => {
        if (el.dataset.optionValueId !== option.dataset.optionValueId) {
          el.checked = false;
          el.classList.remove("selected");
        } else {
          el.checked = true;
          el.classList.add("selected");
          this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        }
      })
      this.reRenderProducSwatchs(this.resolveVariant(idx, option.value));
    }

    // Option index -> handles of the values filtered on in the page URL
    // (e.g. filter.v.option.taille=XXS&filter.v.option.taille=T4).
    getActiveOptionFilters() {
      const filters = new Map();
      const params = new URLSearchParams(window.location.search);
      (this.cardData?.options || []).forEach((optionName, index) => {
        const values = params.getAll(`filter.v.option.${handleize(optionName)}`).filter(Boolean).map(handleize);
        if (values.length > 0) filters.set(index, values);
      });
      return filters;
    }

    // The variant shown once the value at `optionIndex` is clicked, kept in step with
    // snippets/product-card-variant.liquid: in stock and matching the active filters first,
    // then a filtered size even sold out rather than an unrelated size in stock, and the
    // currently shown values of the other options whenever possible.
    resolveVariant(optionIndex, value) {
      const variants = this.cardData?.variants || [];
      const candidates = variants.filter(variant => variant.options[optionIndex] === value);
      if (candidates.length === 0) return null;

      const current = variants.find(variant => variant.id === this.currentVariantId);
      const filters = this.getActiveOptionFilters();
      const matchesFilters = variant => Array.from(filters).every(([index, values]) => values.includes(handleize(variant.options[index])));
      const keepsOtherOptions = variant => !current || variant.options.every((optionValue, index) => index === optionIndex || optionValue === current.options[index]);

      const preferences = [
        variant => variant.available && matchesFilters(variant) && keepsOtherOptions(variant),
        variant => variant.available && matchesFilters(variant),
        variant => matchesFilters(variant) && keepsOtherOptions(variant),
        matchesFilters,
        variant => variant.available && keepsOtherOptions(variant),
        variant => variant.available,
        keepsOtherOptions,
      ];
      for (const isPreferred of preferences) {
        const variant = candidates.find(isPreferred);
        if (variant) return variant;
      }
      return candidates[0];
    }

    async reRenderProducSwatchs(variant) {
      // Without variant data (older markup), fall back to Shopify's own option resolution.
      const query = variant
        ? `variant=${variant.id}`
        : `option_values=${this.optionsSelectedValues.join(",")}`;
      const url = `${this.productUrl}?${query}&section_id=product-swatch`;
      let dataHTML = ProductCardSwatchOption.cacheData[url];
      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ProductCardSwatchOption.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
          return;
        }
      }

      this.updateElements(this.productCardWrapperEle, dataHTML, ".current-variant");
      this.updateCurrentVariant(dataHTML);
      if (this.isShowEDT) {
        this.updateElements(this.productCardWrapperEle, dataHTML, ".estimate-delivery-card-msg");
      }
      this.updateLabelData(this.productCardWrapperEle, dataHTML)
      this.renderSwatchOptionStyle(dataHTML, this.productCardWrapperEle);
      this.updateImage(dataHTML);
      this.updatePrice(dataHTML);
    }

    // Keep the card pointing at the variant it now shows: every link opens the PDP on it,
    // and the bundle picker reads the full list of selected option value ids.
    updateCurrentVariant(dataHTML) {
      const variantId = Number(dataHTML.querySelector(".current-variant")?.textContent.trim());
      if (!variantId) return;

      this.currentVariantId = variantId;
      this.dataset.variantId = variantId;
      this.productCardWrapperEle.dataset.currentVariantId = variantId;

      const selectedValueIds = Array.from(dataHTML.querySelectorAll("#product-swatch .variant-picker__product-option"))
        .map(optionEle => optionEle.querySelector(".swatch-option__value.selected")?.dataset.optionValueId)
        .filter(Boolean);
      if (selectedValueIds.length > 0) {
        this.productCardWrapperEle.dataset.listOptionValuesIdSelected = selectedValueIds.join(",");
      }

      const productPath = new URL(this.productUrl, window.location.origin).pathname;
      this.productCardWrapperEle.querySelectorAll("a[href]").forEach(link => {
        const url = new URL(link.getAttribute("href"), window.location.origin);
        if (url.pathname !== productPath) return;
        url.searchParams.set("variant", variantId);
        link.setAttribute("href", `${url.pathname}${url.search}`);
      });
    }

    updateLabelData(rootEl, dataHTML) {
      const linkVariantEle = rootEl.querySelector(".link-product-variant");
      if (!linkVariantEle) return;

      const currentLabelData = xParseJSON(linkVariantEle.getAttribute("x-labels-data"));
      if (!currentLabelData) return;

      const metafieldDataset = dataHTML.querySelector("#product-swatch")?.dataset.metafieldLabel;
      if (metafieldDataset) {
        const metafieldDataObj = xParseJSON(metafieldDataset);
        currentLabelData.metafield_label = metafieldDataObj.metafield_label;
      }

      this.updateLabelPriceData(currentLabelData, dataHTML);

      linkVariantEle.setAttribute("x-labels-data", JSON.stringify(currentLabelData));
      Alpine.store("xLabelsAndBadges")?.load(linkVariantEle);
    }

    // Refresh the price data the sale badge is computed from, so the discount
    // percentage always reflects the variant that is currently selected.
    updateLabelPriceData(labelData, dataHTML) {
      const variantId = Number(dataHTML.querySelector(".current-variant")?.textContent.trim());
      if (!variantId || !Array.isArray(labelData.variants)) return;

      const variant = labelData.variants.find(v => v.id === variantId);
      if (!variant) return;

      const price = variant.price || 0;
      const compareAtPrice = variant.compare_at_price || 0;
      const saleAmount = compareAtPrice > price ? compareAtPrice - price : 0;
      const currencyFormat = window.Maximize?.currencyFormat;

      labelData.price = price;
      // Anything not strictly above the price means "no discount": the sale
      // badge is hidden by `canShow()` in labels-and-badges.js.
      labelData.compare_at_price = saleAmount > 0 ? compareAtPrice : 0;
      if (currencyFormat) {
        labelData.price_with_currency = formatMoney(price, currencyFormat);
        // "-1" is the no-discount marker used by snippets/data-for-label.liquid.
        labelData.sale_amount = saleAmount > 0 ? formatMoney(saleAmount, currencyFormat) : "-1";
      }
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElement = rootEl.querySelector(`${selector}`);
      const newElement = dataHTML.querySelector(`${selector}`);

      if (oldElement && newElement) {
        oldElement.innerHTML = newElement.innerHTML;
      }
    }

    renderSwatchOptionStyle(dataHTML, productCardWrapper) {
      const labels = productCardWrapper.querySelectorAll(".swatch-option__label");

      labels.forEach((label) => {
        const dataIndex = label.dataset.position;
        const sourceLabel = dataHTML.querySelector(`.swatch-option__label[data-position="${dataIndex}"]`);

        if (sourceLabel?.hasAttribute("style")) {
          mergeSwatchOptionStyle(label, sourceLabel.getAttribute("style"));
        }

        const input = label.previousElementSibling;
        const newInput = sourceLabel?.previousElementSibling;

        if (input && newInput) {
          setSwatchOptionDisabled(input, label, newInput.classList.contains('disabled'));
          input.classList[newInput.classList.contains('selected') ? 'add' : 'remove']('selected');
          input.checked = newInput.classList.contains('selected');
        }
      });
    }

    updateImage(dataHTML) {
      if (this.updateMediaGroup(dataHTML)) return;

      const img = this.productCardWrapperEle.querySelector(".slide-item.is-active .product-card__image");
      let newImg = dataHTML?.querySelector(`.product-card__image`);
      const activeSlide = this.productCardWrapperEle.querySelector(".slide-item.is-active");
      let newMediaId = null;
      if (newImg) {
        const _selected = this.productCardWrapperEle.querySelector(`.swatch-option__value.selected`);
        newMediaId = _selected?.dataset.mediaId || newImg.dataset.mediaId;
      }
      if (this.effectImage == "carousel") {
        const slideToActive = this.productCardWrapperEle.querySelector(`.slide-item[data-media-id="${newMediaId}"]`);
        if (activeSlide && slideToActive) {
          activeSlide.classList.remove("is-active");
          slideToActive.classList.add("is-active")
          return;
        }
      }

      if (img && newImg) {
        img.setAttribute("srcset", newImg.getAttribute("srcset"));
        img.setAttribute("src", newImg.getAttribute("src"));
        if (activeSlide && this.effectImage == "carousel") {
          activeSlide.dataset.mediaId = newMediaId
        }
      }
    }

    // The card gallery belongs to one colour at a time. product-swatch.liquid renders
    // the full media group of the newly selected colour (main image first), so every
    // slide is repointed here rather than only the visible one.
    updateMediaGroup(dataHTML) {
      const slideContainer = this.productCardWrapperEle.querySelector(".product-card__image-wrapper .link-product-variant");
      if (!slideContainer) return false;

      const newImages = Array.from(dataHTML?.querySelectorAll(".product-card__media-group .product-card__media-group-item") || []);
      const slides = Array.from(slideContainer.querySelectorAll(":scope > .slide-item"));
      if (newImages.length === 0 || slides.length === 0) return false;

      slides.forEach((slide, index) => {
        const image = slide.querySelector(".product-card__image");
        if (!image) return;

        // A colour can carry fewer images than the card has slides. The slider caches
        // its slide list at init, so repeat the last image instead of leaving a blank.
        const source = newImages[Math.min(index, newImages.length - 1)];
        image.setAttribute("src", source.getAttribute("src"));
        image.setAttribute("srcset", source.getAttribute("srcset"));
        image.setAttribute("alt", source.getAttribute("alt"));
        slide.dataset.mediaId = source.dataset.mediaId;

        // The main image of the selected colour is always the first slide.
        slide.classList[index === 0 ? "add" : "remove"]("is-active");
      });

      const indicators = this.productCardWrapperEle.querySelectorAll(".slider-indicators .slider-indicator");
      indicators.forEach((indicator, index) => {
        indicator.classList[index === 0 ? "add" : "remove"]("active");
      });

      return true;
    }

    updatePrice(dataHTML) {
      const priceWrapper = this.productCardWrapperEle.querySelector(".product-card__price");
      const newPriceWrapper = dataHTML.querySelector(".current-variant__price");
      if (priceWrapper && newPriceWrapper) {
        priceWrapper.innerHTML = newPriceWrapper.innerHTML;
      }
    }
  }

  if (!customElements.get("product-card-swatch-option")) {
    customElements.define("product-card-swatch-option", ProductCardSwatchOption);
  }

  class ChooseOptionElement extends HTMLElement {
    static cacheData = {};
    constructor() {
      super();
      this.optionInputElements = Array.from(this.querySelectorAll(`.swatch-option__value`));
      this.optionLabelElements = Array.from(this.querySelectorAll(`.swatch-option__label`));
      this.optionsSelectedValues = [];
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.chooseOptionElementId = this.dataset.chooseOptionElementId;
      const listSelectedOptionValue = this.optionInputElements.filter(ele => ele.classList.contains('selected'));
      listSelectedOptionValue.forEach((input) => {
        const position = input.dataset.optionPosition;
        this.optionsSelectedValues[position - 1] = input.dataset.optionValueId;
      })

      this.currentVariantInfoEle = this.querySelector('[type="application/json"][data-type="current-variant-info"]');
      this.currentVariantAdditionalInfoEle = this.querySelector('[type="application/json"][data-type="current-variant-additional-info"]');
      if (this.currentVariantInfoEle) {
        this.currentVariant = JSON.parse(this.currentVariantInfoEle?.textContent);
      }
    }

    connectedCallback() {
      this.optionLabelElements.forEach(el => {
        el.addEventListener('click', this.handleClickOptionLabel.bind(this));

        el.addEventListener('keyup', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            this.handleClickOptionLabel.call(this, e);
          }
        })
      })
    }


    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;
      let listInputSamePosition = this.optionInputElements.filter((el) => el.dataset.optionPosition === position);
      const selectedValue = this.querySelector(`.seclected-value-${position}`);
      if (selectedValue) {
        selectedValue.textContent = option.dataset.value;
      }

      listInputSamePosition.forEach((el) => {
        if (el.dataset.optionValueId !== option.dataset.optionValueId) {
          el.checked = false;
          el.classList.remove("selected");
        } else {
          el.checked = true;
          el.classList.add("selected");
          this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        }
      })

      this.reRenderChooseOption();
    }

    async reRenderChooseOption() {
      const variantPickerEle = this.closest('.variant-picker__wrapper');
      const indexParam = variantPickerEle?.dataset?.indexParam;
      const chooseOptionWrapper = this.closest(".choose-option");
      const buyButton = chooseOptionWrapper.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) {
        buyButton.disabled = true
      }
      const url = `${this.productUrl}?option_values=${this.optionsSelectedValues.join(",")}&section_id=choose-option&page=${indexParam}`;
      let dataHTML = ChooseOptionElement.cacheData[url];
      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ChooseOptionElement.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
        }
      }

      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-option__price-wrapper");
      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-options-content .choose-option__properties-group");
      let newCurrentVariantInfoEle = dataHTML.querySelector('[type="application/json"][data-type="current-variant-info"]');
      if (newCurrentVariantInfoEle) {
        this.currentVariant = JSON.parse(newCurrentVariantInfoEle?.textContent);
        this.currentVariantInfoEle.textContent = newCurrentVariantInfoEle?.textContent
      }
      let newCurrentVariantAdditionalInfoEle =  dataHTML.querySelector('[type="application/json"][data-type="current-variant-additional-info"]');
      if (this.currentVariantAdditionalInfoEle && newCurrentVariantAdditionalInfoEle) {
        this.currentVariantAdditionalInfoEle.textContent = newCurrentVariantAdditionalInfoEle.textContent;
      }

      if (this.currentVariant && this.productId) {
        const _upc = document.querySelectorAll(`[data-parent-id="${this.productId}"][name="properties[_upc]"]`);
        if(_upc && this.currentVariant?.barcode && _upc.length > 0)
        Array.from(_upc).forEach(input => { input.value = this.currentVariant.barcode; });

        const _compare_at_price = document.querySelectorAll(`[data-parent-id="${this.productId}"][name="properties[_compare_at_price]"]`);
        if(_compare_at_price && this.currentVariant?.compare_at_price && _compare_at_price.length > 0)
        Array.from(_compare_at_price).forEach(input => { input.value = '$'+(this.currentVariant.compare_at_price / 100).toFixed(2); });

        const _price = document.querySelectorAll(`input[data-parent-id="${this.productId}"][name="properties[_price]"]`);
        if(_price && this.currentVariant?.price && _price.length > 0)
        Array.from(_price).forEach(input => { input.value = '$'+(this.currentVariant.price / 100).toFixed(2); });

      }

      this.renderSwatchOptionStyle(dataHTML, chooseOptionWrapper);
      if (buyButton && !buyButton.ariaDisabled) {
        buyButton.disabled = false
      }
      this.dispatchChangeVariantEvent();
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElement = rootEl.querySelector(`${selector}`);
      const newElement = dataHTML.querySelector(`${selector}`);

      if (oldElement && newElement) {
        oldElement.innerHTML = newElement.innerHTML;
      }
    }

    renderSwatchOptionStyle(dataHTML, productCardWrapper) {
      const labels = productCardWrapper.querySelectorAll(".swatch-option__label");

      labels.forEach((label) => {
        const dataIndex = label.dataset.position;
        const sourceLabel = dataHTML.querySelector(`.swatch-option__label[data-position="${dataIndex}"]`);

        if (sourceLabel?.hasAttribute("style")) {
          mergeSwatchOptionStyle(label, sourceLabel.getAttribute("style"));
        }

        const input = label.previousElementSibling;
        const newInput = sourceLabel?.previousElementSibling;

        if (input && newInput) {
          input.classList[newInput.classList.contains('disabled') ? 'add' : 'remove']('disabled');
          input.classList[newInput.classList.contains('selected') ? 'add' : 'remove']('selected');
          input.checked = newInput.classList.contains('selected');
        }
      });
    }
    dispatchChangeVariantEvent() {
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${this.chooseOptionElementId}`, {
          bubbles: true,
          detail: {
            productId: this.productId,
            currentVariant: this.currentVariant
          },
        })
      );
    }
  }

  if (!customElements.get("choose-option")) {
    customElements.define("choose-option", ChooseOptionElement);
  }
}
